// ─── Chase ⋮ menu — which options a row offers, by status ─────────────────────
//
//   Mute / Unmute              Active OR Lapsed  (flips on chase.mute)
//   Mark as saved (auto-renew) Active, but NOT Auto-Renewals
//   Set to Lapse               Active OR Payment-Failed
//   Mark as Renewed            Payment-Received (paid, agent renews it)
//                              OR Auto-Renewals (the auto payment collected)
//   Mark as Renewed (won back) Lapsed OR Payment-Failed
//   Archive / Unarchive        always  (flips on chase.archived)
//   Edit Chase                 always
//
// Every transition is a PATCH /chase/:id body; mute has its own toggle route and
// edit opens the modal, so those two carry no patch.

import moment from "moment";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowsRotate,
  faBell,
  faBellSlash,
  faBoxArchive,
  faBoxOpen,
  faCircleCheck,
  faCreditCard,
  faHashtag,
  faPenToSquare,
  faTriangleExclamation,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";
import type { IChase } from "../types/chase-type";
import type { ConfirmSelect } from "../providers/confirmation-model";

/** Spec name → the status string this app actually stores on a chase. */
export const STATUS = {
  INVITING: "Inviting",
  AUTO_RENEWAL: "Auto-Renewals",
  PAYMENT_RECEIVED: "Payment-Received",
  RENEWED: "Renewed",
  PAYMENT_FAILED: "Payment-Failed",
  LAPSED: "Lapsed",
} as const;

export const ACTIVE_STATUSES: string[] = [
  STATUS.INVITING,
  STATUS.AUTO_RENEWAL,
];

/**
 * Why the policy lapsed — asked for whenever a chase is set to lapse, because
 * it decides whether we win them back.
 *
 * The first two STOP the win-back run: they must stay byte-identical to
 * `NO_WIN_BACK_REASONS` in server/src/utils/timeline/win-back-job.ts, which
 * matches on the stored string.
 */
export const LAPSE_REASONS = [
  { value: "Customer asked not to renew", label: "Customer asked not to renew" },
  { value: "Switching broker", label: "Switching broker" },
  { value: "Price too high", label: "Price too high" },
  { value: "No longer trading", label: "No longer trading" },
  { value: "Cover no longer needed", label: "Cover no longer needed" },
  { value: "Could not contact customer", label: "Could not contact customer" },
  { value: "Other", label: "Other" },
];

/** Muting is offered while a chase is still running, or once it has dropped out. */
export const canMute = (chase?: IChase | null): boolean => {
  const status = String(chase?.status ?? "");
  return ACTIVE_STATUSES.includes(status) || status === STATUS.LAPSED;
};

// ─── Take-payment panel ───────────────────────────────────────────────────────
// A chase can still be paid while it's running; once it has settled one way or
// the other the panel states where it ended up instead of offering a button.

type ChaseLike = { status?: string; paymentType?: string } | null | undefined;

export const canTakePayment = (chase: ChaseLike): boolean =>
  ACTIVE_STATUSES.includes(String(chase?.status ?? ""));

/** Monthly Direct Debit is confirmed, not collected. */
export const paymentLabel = (chase: ChaseLike): string =>
  chase?.paymentType === "instalments" ? "Confirm Renewal" : "£ Take Payment";

/** The customer-facing pay page — public route of this same app. */
// The version tells the page which chase on the reference to open.
export const payLink = (
  policyReferenceNumber?: string,
  versionReferenceNumber?: string | null,
) =>
  `${window.location.origin}/pay/${policyReferenceNumber ?? ""}${
    versionReferenceNumber
      ? `?version=${encodeURIComponent(versionReferenceNumber)}`
      : ""
  }`;

/** Live version references start with 1 — 9 (or anything else) is a quote ref. */
export const isLiveVersion = (version?: string | null): boolean =>
  String(version ?? "").trim().startsWith("1");

/** "1.4 MB" — file sizes are only ever shown, never computed with. */
export const fileSize = (bytes?: number | null): string =>
  !bytes
    ? "—"
    : bytes < 1024
      ? `${bytes} B`
      : bytes < 1024 * 1024
        ? `${(bytes / 1024).toFixed(0)} KB`
        : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export type StatusNote = { label: string; dot: string };

const ENDED = "bg-slate-400 dark:bg-slate-500";

const STATUS_NOTES: Record<string, StatusNote> = {
  [STATUS.PAYMENT_RECEIVED]: {
    label: "Payment received — awaiting renewal",
    dot: "bg-indigo-400",
  },
  [STATUS.RENEWED]: { label: "Policy renewed", dot: "bg-emerald-500" },
  [STATUS.LAPSED]: { label: "Policy lapsed", dot: ENDED },
  [STATUS.PAYMENT_FAILED]: {
    label: "Payment failed — follow up",
    dot: "bg-amber-500",
  },
};

// ─── DUE TO AUTO pot ──────────────────────────────────────────────────────────
// An auto-renewing policy drops in here shortly before renewal — 10 days out on
// cash, 14 on Direct Debit — because the payment should collect itself and
// somebody has to confirm it did. `dueToAutoPot` is set once and never cleared,
// so a chase can't re-pot; what empties the pot is the agent answering, which
// moves the chase off "Auto-Renewals".

export const inAutoPot = (chase?: IChase | null): boolean =>
  !!chase?.dueToAutoPot && chase?.status === STATUS.AUTO_RENEWAL;

// ─── AUTO POT: the payment failed ─────────────────────────────────────────────
// `dueToAutoPot` is never cleared, so it still reads true after the agent answers
// "payment failed" — that pair IS the cohort: an auto policy whose money now has
// to be taken by hand. Nobody chases it a second time on their own, so the box
// prompts a retry every other day.
//
// The clock is the PARITY OF DAYS-TO-RENEWAL, which needs no column and no date:
// it counts itself down at midnight, and pot day (14) is even, so the prompts
// land on 14, 12, 10 … Parity of the timeline's last done day would do the same
// job but flip in the middle of the afternoon, whenever the daily job happened to
// reach this chase.

export const RETRY_EVERY_DAYS = 2;

/** Whole days until renewal — negative once it is behind us. */
export const daysToRenewal = (chase?: IChase | null): number =>
  moment(chase?.renewalDate)
    .startOf("day")
    .diff(moment().startOf("day"), "days");

/** Potted, then the payment failed. */
export const autoPotFailed = (chase?: IChase | null): boolean =>
  !!chase?.dueToAutoPot && chase?.status === STATUS.PAYMENT_FAILED;

/** Today is a nudge day — try to take the money again. */
export const retryDue = (chase?: IChase | null): boolean =>
  autoPotFailed(chase) && daysToRenewal(chase) % RETRY_EVERY_DAYS === 0;

// ─── PAYMENT DECLINED ─────────────────────────────────────────────────────────
// A chase that is still running — Inviting or Auto-Renewals — whose renewal
// payment has been tried at least once. Recording an attempt is what puts it
// here; it leaves as soon as the chase settles (renewed, paid, failed, lapsed).

export const paymentDeclined = (chase?: IChase | null): boolean =>
  ACTIVE_STATUSES.includes(String(chase?.status ?? "")) &&
  Number(chase?.paymentAttempts ?? 0) > 0;

/** Shown in place of the pay button once a chase is no longer payable. */
export const statusNote = (chase: ChaseLike): StatusNote =>
  STATUS_NOTES[String(chase?.status ?? "")] ?? {
    label: "No payment due",
    dot: ENDED,
  };

export type ChaseActionKey =
  | "edit"
  | "version"
  | "mute"
  | "saved"
  | "lapse"
  | "renewed"
  | "archive"
  | "attempts"
  | "auto-renewed"
  | "auto-failed";

export type ChaseAction = {
  key: ChaseActionKey;
  label: string;
  icon: IconDefinition;
  /** PATCH /chase/:id body — absent for `edit` and `mute`. */
  patch?: Record<string, any>;
  /** Appended to /chase/:id when the action has its own route, e.g. "/archive". */
  path?: string;
  /** Ask first, in the action's own words. */
  confirm?: {
    title: string;
    desc: string;
    alert?: string;
    button: string;
    danger?: boolean;
    /** Answer collected in the dialog and merged into `patch`. */
    select?: ConfirmSelect;
  };
  success?: string;
};

/**
 * Asks for the new attempt count — opened from the Payment Attempts box beside
 * the lapse reason. Free number rather than a fixed list: there is no sensible
 * cap on how many times a renewal payment gets retried.
 */
export const attemptsAction = (chase: IChase): ChaseAction => ({
  key: "attempts",
  label: "Update",
  icon: faCreditCard,
  confirm: {
    title: "How many times has the renewal payment been attempted?",
    desc: "Saved on the chase and written to its renewal timeline.",
    button: "Save attempts",
    select: {
      label: "Payment attempts",
      type: "number",
      placeholder: "0",
      defaultValue: String(chase?.paymentAttempts ?? 0),
      field: "paymentAttempts",
      required: true,
    },
  },
  success: "Payment attempts updated",
});

/**
 * The pot's two answers — rendered as buttons above the chase timeline, not in
 * the ⋮ menu. Both are one status change, and that is what drops the chase out
 * of the pot: renewed is done, failed hands it to the chase run.
 */
export const autoPotActions = (chase: IChase): ChaseAction[] => {
  const dd = chase?.paymentType === "instalments";

  return [
    {
      key: "auto-renewed",
      label: "Mark as Renewed",
      icon: faCircleCheck,
      confirm: {
        title: "Did the auto-renewal payment go through?",
        desc: "The policy is recorded as renewed and it leaves the Auto Payment Attempt list.",
        button: "Mark as renewed",
      },
      patch: { status: STATUS.RENEWED },
      success: "Chase marked as renewed",
    },
    {
      key: "auto-failed",
      label: dd ? "Direct Debit Failed" : "Payment Failed",
      icon: faTriangleExclamation,
      confirm: {
        title: dd
          ? "Are you sure the Direct Debit failed?"
          : "Are you sure the renewal payment failed?",
        desc: "It leaves the Auto Payment Attempt list and is chased like a non-auto policy until it renews or lapses.",
        button: dd ? "Direct Debit failed" : "Payment failed",
        danger: true,
      },
      patch: { status: STATUS.PAYMENT_FAILED },
      success: dd ? "Direct Debit marked as failed" : "Payment marked as failed",
    },
  ];
};

/**
 * The retry box's answer — the money finally came off, so the policy renews and
 * the prompts stop. Same patch as the pot's "Mark as Renewed", worded for a
 * payment being collected by hand rather than one that should collect itself.
 */
export const retryRenewedAction: ChaseAction = {
  key: "renewed",
  label: "Mark as Renewed",
  icon: faCircleCheck,
  confirm: {
    title: "Has the renewal payment now gone through?",
    desc: "The policy is recorded as renewed and the retry prompts stop.",
    button: "Mark as renewed",
  },
  patch: { status: STATUS.RENEWED },
  success: "Chase marked as renewed",
};

/** The ⋮ options this row should show, in menu order. */
export const rowActions = (chase: IChase): ChaseAction[] => {
  const status = String(chase?.status ?? "");
  const active = ACTIVE_STATUSES.includes(status);
  const lostIt = status === STATUS.LAPSED;

  const actions: ChaseAction[] = [
    { key: "edit", label: "Edit Chase", icon: faPenToSquare },
    {
      key: "version",
      label: "Update Version Reference",
      icon: faHashtag,
      path: "/version",
      confirm: {
        title: "Update Version Reference",
        desc: "Saved on the chase, and its renewal documents are copied under the new version.",
        alert:
          "All previous documents on this chase, both quote and renewal documents, will be moved to the new version reference you enter here.",
        button: "Update",
        select: {
          label: "Version reference number",
          type: "text",
          placeholder: "Paste the new version reference",
          defaultValue: chase?.versionReferenceNumber ?? "",
          field: "versionReferenceNumber",
          required: true,
        },
      },
      success: "Version reference updated",
    },
  ];

  if (canMute(chase))
    actions.push({
      key: "mute",
      label: chase?.mute ? "Unmute" : "Mute",
      icon: chase?.mute ? faBell : faBellSlash,
    });

  if (active && status !== STATUS.AUTO_RENEWAL)
    actions.push({
      key: "saved",
      label: "Mark as saved (auto-renew)",
      icon: faArrowsRotate,
      confirm: {
        title: "Are you sure you want to mark this chase as saved?",
        desc: "The policy switches to auto-renew, so the chase emails and SMS stop.",
        button: "Mark as saved",
      },
      patch: { auto: true, status: STATUS.AUTO_RENEWAL },
      success: "Chase marked as saved",
    });

  if (active || status === STATUS.PAYMENT_FAILED)
    actions.push({
      key: "lapse",
      label: "Set to Lapse",
      icon: faXmarkCircle,
      confirm: {
        title: "Are you sure you want to set this chase to lapse?",
        desc: "The chase stops, the policy is recorded as lapsed, and the win-back offers are scheduled from today.",
        button: "Set to lapse",
        danger: true,
        select: {
          label: "Lapse reason",
          placeholder: "Why did the policy lapse?",
          options: LAPSE_REASONS,
          field: "lapseReason",
          required: true,
        },
      },
      patch: { status: STATUS.LAPSED },
      success: "Chase set to lapse",
    });

  // Paid: the money is in and an agent renews the policy with the insurer.
  // Auto: the payment should have collected itself — same answer as the pot's.
  const paid = status === STATUS.PAYMENT_RECEIVED;
  const auto = status === STATUS.AUTO_RENEWAL;

  if (lostIt || paid || auto || status === STATUS.PAYMENT_FAILED)
    actions.push({
      key: "renewed",
      label: paid || auto ? "Mark as Renewed" : "Mark as Renewed (won back)",
      icon: faCircleCheck,
      confirm: {
        title: auto
          ? "Did the auto-renewal payment go through?"
          : "Are you sure you want to mark this chase as renewed?",
        desc: auto
          ? "The policy is recorded as renewed and it leaves the Auto Payment Attempt list."
          : paid
            ? "The customer has already paid — only confirm once the policy has been renewed with the insurer."
            : "It's recorded as won back and the chase stops.",
        button: "Mark as renewed",
      },
      patch: { status: STATUS.RENEWED },
      success: "Chase marked as renewed",
    });

  actions.push(
    chase?.archived
      ? {
          key: "archive",
          label: "Unarchive Chase",
          icon: faBoxOpen,
          confirm: {
            title: "Are you sure you want to unarchive this chase?",
            desc: "It comes back into the chase list, with its history, and starts sending again.",
            button: "Unarchive",
          },
          path: "/archive",
          success: "Chase unarchived",
        }
      : {
          key: "archive",
          label: "Archive Chase",
          icon: faBoxArchive,
          confirm: {
            title: "Are you sure you want to archive this chase?",
            desc: "It moves to Customer Archives, stops sending, and its history is parked. You can unarchive it later.",
            button: "Archive",
          },
          path: "/archive",
          success: "Chase archived",
        }
  );

  return actions;
};
